<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<script setup lang="ts">
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {useProfileDateFormat} from '@/ts/composables/stateful/use-profile-date-format';

interface SessionWithUnlock {
  unlockDate: string;
  timeUntilUnlockMillis: number;
}

const props = withDefaults(defineProps<{
  session: SessionWithUnlock | null;
  variant?: 'banner' | 'card';
}>(), {variant: 'banner'});

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const MINUTES_PER_HOUR = 60;

const {t: $t, locale} = useI18n();
const datePatterns = useProfileDateFormat();
const unlockDate = computed<string | null>(() => {
  const remaining = props.session?.timeUntilUnlockMillis;
  if (!remaining) {
    return null;
  }
  // More than a day out: show the absolute unlock date (localized short month). Two English
  // sentences rather than one, because the order lives inside the sentence and this date follows
  // the profile's country like every other (IPD-3229) — the German one is day-first either way, so
  // both keys carry the same words there.
  if (remaining > DAY_MS) {
    const date = new Date(props.session!.unlockDate);
    const monthShort = new Intl.DateTimeFormat(locale.value, {month: 'short'}).format(date);
    const key = datePatterns.value.isMonthFirst ? 'session_unlocks_date' : 'session_unlocks_date_day_first';
    return $t(key, {day: date.getDate(), monthShort});
  }
  // Within a day: count down in whole hours. Round to nearest so 1h05m reads
  // as "1 hour" rather than ceil's misleading "2 hours".
  if (remaining > HOUR_MS) {
    const hours = Math.round(remaining / HOUR_MS);
    return $t('session_unlocks_h', hours, {named: {hour: hours}});
  }
  // Within the hour: count down in whole minutes, rounded up so the chip never
  // shows "0 minutes" before unlock. A rounded-up 60 reads better as "1 hour".
  const minutes = Math.ceil(remaining / MINUTE_MS);
  if (minutes === MINUTES_PER_HOUR) {
    return $t('session_unlocks_h', 1, {named: {hour: 1}});
  }
  return $t('session_unlocks_m', minutes, {named: {min: minutes}});
});
</script>
<template>
  <div v-if="unlockDate && props.variant === 'card'" class="unlocks card">
    <svg-illustration name="lock.svg"/> {{ unlockDate }}
  </div>
  <div v-else-if="unlockDate" class="unlocks banner">
    {{ unlockDate }}
  </div>
</template>
<style lang="scss" scoped>
.unlocks.banner {
  border-radius: var(--button-border-radius);
  border: 2px solid var(--Neutral-UI-neutral-ui-70);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  padding: var(--Spacing-spacing-3) var(--Spacing-spacing-7);
  @include font-body-bold;
}

// Lock icon + countdown, sitting at the bottom of the session card at the same
// level and style as the "Session Complete" label (see intervention-landing-session).
.unlocks.card {
  @include font-body-sm-bold;
  display: flex;
  align-items: center;
  gap: var(--Spacing-spacing-1);
  min-height: var(--Spacing-spacing-7);
}
</style>
